import {
  createQuestionAnswersColumns,
  type AnswerForReview,
} from "./-components/question-answers-columns"
import { ReviewAnswerDialog } from "./-components/review-answer-dialog"
import { useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { z } from "zod"
import { ChevronLeft } from "lucide-react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { SafeHtml } from "@/components/ui/safe-html"
import { DataTable } from "@/components/ui/data-table"
import { Badge } from "@/components/ui/badge"

const questionReviewSearchSchema = z.object({
  quizstates: z.string().default(""),
})

export const Route = createFileRoute(
  "/(admin)/admin/quizzes/$quizId_/questions_/$questionId"
)({
  component: RouteComponent,
  validateSearch: (search) => questionReviewSearchSchema.parse(search),
})

function RouteComponent() {
  const { quizId, questionId } = Route.useParams()
  const { quizstates } = Route.useSearch()
  const [selectedAnswer, setSelectedAnswer] = useState<AnswerForReview | null>(
    null
  )
  const [dialogOpen, setDialogOpen] = useState(false)

  // Parse quiz state IDs from comma-separated string
  const quizStateIds = quizstates
    .split(",")
    .filter((id) => id.length > 0) as Id<"quizState">[]

  const questionData = useQuery(
    api.questions.handlers.getQuestionAnswersForReview,
    quizStateIds.length > 0
      ? {
          questionId: questionId as Id<"questions">,
          quizStateIds,
        }
      : "skip"
  )

  const handleReview = (answer: AnswerForReview) => {
    setSelectedAnswer(answer)
    setDialogOpen(true)
  }

  const columns = createQuestionAnswersColumns(handleReview)

  if (quizStateIds.length === 0) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link
              to="/admin/quizzes/$quizId/questions"
              params={{ quizId }}
              search={{ quizstates }}
            >
              <ChevronLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-3xl font-bold">Question Review</h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              No quiz states selected. Please go back and select quiz instances
              to review.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (questionData === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  const { question, answers } = questionData

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link
            to="/admin/quizzes/$quizId/questions"
            params={{ quizId }}
            search={{ quizstates }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Question Review</h1>
          <p className="text-sm text-muted-foreground">
            Reviewing {answers.length} answer(s)
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Question Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Question
            </p>
            <p className="text-lg">
              <SafeHtml html={question.text} />
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Correct Answer
            </p>
            <Badge
              variant="default"
              className="bg-green-500 text-base px-3 py-1"
            >
              <SafeHtml html={question.answer} />
            </Badge>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Category
            </p>
            <Badge variant="outline">{question.category}</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Answers Submitted</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable columns={columns} data={answers} />
        </CardContent>
      </Card>

      {selectedAnswer && (
        <ReviewAnswerDialog
          answer={selectedAnswer}
          correctAnswer={question.answer}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </div>
  )
}
